import React, { useState, useEffect, useMemo } from "react";
import { Divider, Spin } from "antd";
import { formatDate } from "../../../../utils/dateConversion";
import Table from "../../../../components/atoms/Tables/Table";
import TableLoading from "../../../../components/Loading/TableLoading";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";
import Dashboard from "../../../../api/Dashboard";
import PaginationView from "../../../../components/molecules/Pagination";

const PAGE_SIZE = 15;

const RegistrationsView = () => {
  const [registrationsData, setRegistrationsData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [isCityLoading, setIsCityLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [statsData, setStatsData] = useState({
    last24HoursCount: 0,
    last3DaysCount: 0,
    last7DaysCount: 0,
    last30DaysCount: 0,
    last14DaysCount: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });

  useEffect(() => {
    const getPieChartData = async () => {
      try {
        setIsCityLoading(true);
        const response = await Dashboard.getCityCount();
        const cityCountData = response?.data?.data || [];
        const transformedCityData = cityCountData
          ?.map((item) => ({
            name: item.city || "Unknown",
            value: item.count || 0,
          }))
          .filter((item) => item.value > 0);
        setCityData(transformedCityData);
      } catch (error) {
        console.error("Error fetching city count data:", error);
        setCityData([]);
      } finally {
        setIsCityLoading(false);
      }
    };

    getPieChartData();
  }, []);

  // Fetch count only on initial load
  useEffect(() => {
    const fetchRegistrationsCount = async () => {
      try {
        const response = await Dashboard.getJobAnalyticsCount("registration");
        const counts = response?.data?.data || {};

        const updatedStatsData = {
          last24HoursCount: counts["24HoursCount"] || 0,
          last3DaysCount: counts["3DaysCount"] || 0,
          last7DaysCount: counts["7DaysCount"] || 0,
          last30DaysCount: counts["30DaysCount"] || 0,
          last14DaysCount: counts["14DaysCount"] || 0,
        };

        setStatsData(updatedStatsData);
        setPagination((prev) => ({
          ...prev,
          total: counts["14DaysCount"] || 0,
        }));

        // Fetch initial registrations data
        fetchRegistrationsData(1, PAGE_SIZE);
      } catch (error) {
        console.error("Error fetching registrations count:", error);
      }
    };

    fetchRegistrationsCount();
  }, []);

  // Fetch registrations data on pagination change
  useEffect(() => {
    fetchRegistrationsData(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const fetchRegistrationsData = async (page = 1, limit = PAGE_SIZE) => {
    setIsLoading(true);
    try {
      const response = await Dashboard.getRecentSeekerRegistrations(
        page,
        limit
      );
      const allRegistrations = response.data.data;

      // Filter registrations for the last 14 days
      const now = new Date();
      const last14Days = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      const filteredRegistrations = allRegistrations.filter(
        (reg) => new Date(reg.created_at) >= last14Days
      );

      const transformedRegistrations = filteredRegistrations.map((reg) => ({
        id: reg.id,
        applicant_name: `${reg.first_name} ${reg.last_name}`,
        applicant_email: reg.email,
        applicant_phone: reg.phone || "N/A",
        city: reg.city || "N/A",
        state: reg.state || "N/A",
        registration_date: reg.created_at,
        completed_profile: reg.skill !== null && reg.education !== null,
        skills: reg.skill ? reg.skill.split(",") : [],
        position_interests: reg.position_interests
          ? JSON.parse(reg.position_interests)
          : [],
      }));

      setRegistrationsData(transformedRegistrations);
    } catch (error) {
      console.error("Error fetching registrations:", error);
      // Reset to first page if an error occurs
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const generateVibrantColors = (count) => {
    const baseColors = [
      "hsl(220, 80%, 60%)", // Blue
      "hsl(140, 60%, 50%)", // Green
      "hsl(45, 90%, 55%)", // Yellow
      "hsl(10, 80%, 60%)", // Red
      "hsl(280, 70%, 65%)", // Purple
      "hsl(200, 85%, 50%)", // Cyan
      "hsl(300, 65%, 55%)", // Pink
      "hsl(30, 85%, 55%)", // Orange
    ];

    return Array.from(
      { length: count },
      (_, i) => baseColors[i % baseColors.length]
    );
  };

  const dynamicColors = useMemo(
    () => generateVibrantColors(cityData.length),
    [cityData.length]
  );

  return (
    <>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 pt-4">
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 24 Hours
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-purple-600">
            {statsData.last24HoursCount} Registrations
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 3 Days
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-cyan-600">
            {statsData.last3DaysCount} Registrations
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 7 Days
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-pink-600">
            {statsData.last7DaysCount} Registrations
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 30 Days
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-orange-600">
            {statsData.last30DaysCount} Registrations
          </dd>
        </div>
      </div>

      <div className="pt-6 pb-4">
        <Divider className="my-0" />
        <p className="mt-4 font-bold text-lg text-gray-500">
          Registrations from last 14 days
        </p>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <TableLoading Columns={5} />
        ) : (
          <>
            <Table
              headers={
                registrationsData?.length > 0
                  ? ["User", "Location", "Registered On"]
                  : ["No registrations found in the last 14 days"]
              }
              data={registrationsData?.map((registration, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-3 pr-3 text-sm">
                    <div className="font-medium text-gray-900">
                      {registration.applicant_name}
                    </div>
                    <div className="text-gray-500">
                      {registration.applicant_email}
                    </div>
                    <div className="text-gray-500">
                      {registration.applicant_phone}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm">
                    <div className="text-gray-900">
                      {registration.city}, {registration.state}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm text-gray-500">
                    {formatDate(
                      registration.registration_date,
                      "MM-DD-YYYY HH:mm"
                    )}
                  </td>
                </tr>
              ))}
            />
            <PaginationView
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={statsData.last14DaysCount}
              onChange={handlePaginationChange}
              className="mt-6 flex justify-end"
            />
          </>
        )}
      </div>

      {/* Pie Chart Section */}
      <div className="pt-6 pb-4">
        <Divider className="my-0" />
        <p className="mt-4 font-bold text-lg text-gray-500">
          Job Seeker Registrations by City (Last 30 Days)
        </p>
      </div>

      <div className="mt-6 flex justify-center">
        {isCityLoading ? (
          <div className="flex justify-center items-center h-[450px] w-full">
            <Spin size="large" />
          </div>
        ) : cityData.length > 0 ? (
          <div className="w-full">
            <ResponsiveContainer width="100%" height={450}>
              <PieChart>
                <Pie
                  data={cityData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="85%"
                  activeIndex={activeIndex}
                >
                  {cityData.map((city, index) => {
                    const baseColor = dynamicColors[index];
                    const brightenedColor = baseColor?.replace("%60%", "%75%");

                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          activeIndex === index ? brightenedColor : baseColor
                        }
                        opacity={
                          activeIndex === null || activeIndex === index
                            ? 1
                            : 0.4
                        }
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                      />
                    );
                  })}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const percent = (
                        (data.value / statsData.last30DaysCount) *
                        100
                      ).toFixed(1);
                      return (
                        <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
                          <p className="font-semibold text-gray-900">
                            {data.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            Registrations: {data.value} ({percent}%)
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-3 mt-3">
              {cityData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center cursor-pointer"
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  <span
                    className="inline-block w-4 h-4 mr-2 rounded-full"
                    style={{
                      backgroundColor: dynamicColors[index],
                    }}
                  />
                  <span className="text-sm text-black">
                    {item.name} ({item.value})
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center">No city data available</p>
        )}
      </div>
    </>
  );
};

export default RegistrationsView;
